import { t } from 'i18next';
import { MainLayout } from '../../Layout/MainLayout';
import {
  Currency,
  PricingFee,
  LinearUsage,
  ListContentIsland,
  Money,
  Network,
  PricingListItem,
  Stack,
} from '@razz-team/ui-web';
import { formatDate } from '@razz-team/utils';
import { useBusinessSettingsQuery } from '../../../api/businesses';
import { MainBreadcrumbs } from '../../Layout/MainBreadcrumbs';
import { useAccountsQuery } from '../../../api/accounts';
import { useCardDesignListQuery, useCardListQuery } from '../../../api/cards';
import { Fragment, useMemo } from 'react';
import { countBy, groupBy } from 'lodash-es';
import { Card } from '../CreateCardPage/Card';

export function PricingPage() {
  const cardsQuery = useCardListQuery();
  const cardDesignsQuery = useCardDesignListQuery();
  const accountsQuery = useAccountsQuery();
  const businessSettingsQuery = useBusinessSettingsQuery();
  const plan = businessSettingsQuery.data?.products;

  const accountsCount = useMemo(
    () => countBy(accountsQuery.data ?? [], (it) => it.balance.currency),
    [accountsQuery.data],
  );

  const cardsCount = useMemo(
    () => countBy(cardsQuery.data ?? [], (it) => it.cardIssuer),
    [cardsQuery.data],
  );

  const designsByCondition = useMemo(
    () => groupBy(cardDesignsQuery.data ?? [], (design) => design.conditionId),
    [cardDesignsQuery.data],
  );

  return (
    <MainLayout
      title={
        <MainBreadcrumbs
          title={t('Pricing')}
          parent={{
            to: '/account/business',
            title: t('Your business'),
          }}
        />
      }
      compact
    >
      <ListContentIsland>
        <PricingListItem
          label={t('Current monthly limits are valid for')}
          value={`${formatDate(plan?.periodStartDate, 'MMM dd')} - ${formatDate(plan?.periodEndDate, 'MMM dd')}`}
          loading={businessSettingsQuery.isPending}
        />
        <PricingListItem
          label={t('Currency of limits and fees')}
          value={plan?.baseCurrency}
          loading={businessSettingsQuery.isPending}
        />
        <PricingListItem
          label={t('Service {{period}}', { period: plan?.serviceProduct.period.toLowerCase() })}
          value={<Money value={plan?.serviceProduct.price} />}
          loading={businessSettingsQuery.isPending}
        />
      </ListContentIsland>
      {plan?.fxProduct ? (
        <ListContentIsland title={t('Exchange')}>
          <PricingListItem
            label={t('Fee')}
            value={
              <PricingFee feePct={plan.fxProduct.feePct} freeLimit={plan.fxProduct.freeFxLimit} />
            }
          />
        </ListContentIsland>
      ) : null}
      {plan?.virtualCardProduct ? (
        <ListContentIsland title={t('Virtual cards')}>
          {plan.virtualCardProduct.conditions.map((it) => (
            <Fragment key={it.cardIssuer}>
              <Stack direction="row" spacing={2} padding={4} paddingBottom={0}>
                {designsByCondition[it.id]?.map((design) => (
                  <Card
                    key={design.id}
                    cardDesign={design}
                    holderName={undefined}
                    sx={{ width: 1280 / 25, height: 804 / 25, borderRadius: 1 }}
                  />
                ))}
              </Stack>
              <PricingListItem
                key={it.cardIssuer}
                label={t('Cards limit')}
                value={<LinearUsage used={cardsCount[it.cardIssuer] ?? 0} total={it.totalLimit} />}
              />
              <PricingListItem
                label={t('Issue fee')}
                value={<PricingFee fee={it.issueFee} freeLimitCount={it.freeLimit} />}
              />
            </Fragment>
          ))}
        </ListContentIsland>
      ) : null}
      {plan?.fiatAccountProduct ? (
        <ListContentIsland title={t('Fiat accounts')}>
          <PricingListItem
            label={t('Maximum balance')}
            value={<Money value={plan.fiatAccountProduct.maxBalance} />}
          />
          {plan.fiatAccountProduct.conditions.map((it) => (
            <PricingListItem
              key={it.currency}
              label={<Currency currency={it.currency} withAvatar avatarSize={18} />}
              value={<LinearUsage used={accountsCount[it.currency] ?? 0} total={it.totalLimit} />}
            />
          ))}
        </ListContentIsland>
      ) : null}
      {plan?.cryptoAccountProduct ? (
        <ListContentIsland title={t('Crypto accounts')}>
          {plan.cryptoAccountProduct.conditions.map((it) => (
            <PricingListItem
              key={it.currency}
              label={<Currency currency={it.currency} withAvatar avatarSize={18} />}
              value={<LinearUsage used={accountsCount[it.currency] ?? 0} total={it.totalLimit} />}
            />
          ))}
        </ListContentIsland>
      ) : null}
      {plan?.fiatIncomingExternalTransferProduct ? (
        <ListContentIsland title={t('Fiat incoming transfers')}>
          <PricingListItem
            label={t('Monthly limit')}
            value={<Money value={plan.fiatIncomingExternalTransferProduct.depositMonthlyLimit} />}
          />
          {plan.fiatIncomingExternalTransferProduct.conditions.map((it) => (
            <PricingListItem
              key={it.networkId}
              label={<Network network={it.networkId} withAvatar avatarSize={18} />}
              value={
                <PricingFee
                  fee={it.depositFee}
                  feePct={it.depositFeePct}
                  minFee={it.depositMinFee}
                  maxFee={it.depositMaxFee}
                  freeLimitCount={it.depositFreeLimit}
                  textAfter={t('per incoming transfer')}
                />
              }
            />
          ))}
        </ListContentIsland>
      ) : null}
      {plan?.fiatOutgoingExternalTransferProduct ? (
        <ListContentIsland title={t('Fiat outgoing transfers')}>
          <PricingListItem
            label={t('Monthly limit')}
            value={
              <Money value={plan.fiatOutgoingExternalTransferProduct.withdrawalMonthlyLimit} />
            }
          />
          <PricingListItem
            label={t('Daily limit')}
            value={<Money value={plan.fiatOutgoingExternalTransferProduct.withdrawalDailyLimit} />}
          />
          {plan.fiatOutgoingExternalTransferProduct.conditions.map((it) => (
            <PricingListItem
              key={it.networkId}
              label={<Network network={it.networkId} withAvatar avatarSize={18} />}
              value={
                <PricingFee
                  fee={it.withdrawalFee}
                  feePct={it.withdrawalFeePct}
                  minFee={it.withdrawalMinFee}
                  maxFee={it.withdrawalMaxFee}
                  freeLimitCount={it.withdrawalFreeLimit}
                  textAfter={t('per outgoing transfer')}
                />
              }
            />
          ))}
        </ListContentIsland>
      ) : null}
      {plan?.cryptoIncomingExternalTransferProduct ? (
        <ListContentIsland title={t('Crypto incoming transfers')}>
          {plan.cryptoIncomingExternalTransferProduct.conditions.map((it) => (
            <PricingListItem
              key={it.transferNetwork}
              label={<Network network={it.transferNetwork} withAvatar avatarSize={18} />}
              value={<PricingFee fee={it.depositFee} textAfter={t('per incoming transfer')} />}
            />
          ))}
        </ListContentIsland>
      ) : null}
      {plan?.cryptoOutgoingExternalTransferProduct ? (
        <ListContentIsland title={t('Crypto outgoing transfers')}>
          {plan.cryptoOutgoingExternalTransferProduct.conditions.map((it) => (
            <PricingListItem
              key={it.transferNetwork}
              label={<Network network={it.transferNetwork} withAvatar avatarSize={18} />}
              value={
                <PricingFee
                  fee={it.withdrawalFee}
                  freeLimitCount={it.withdrawalFreeLimit}
                  textAfter={t('per outgoing transfer')}
                />
              }
            />
          ))}
        </ListContentIsland>
      ) : null}
    </MainLayout>
  );
}
